import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getCar, releaseCar } from '../../../api/carsApi';
import { getAtividadesCarro } from '../../../api/atividadeCarroApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { formatReleaseDate } from '../../../utils/releaseDate';
import { notify } from '../../../shared/feedback/notify';
import './VehicleReleaseModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import '../../../shared/feedback/erro-de-tela.css';

interface VehicleReleaseModalProps {
  isOpen: boolean;
  carId: string | null;
  onCancel: () => void;
  onSuccess: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const VehicleReleaseModal: React.FC<VehicleReleaseModalProps> = ({
  isOpen,
  carId,
  onCancel,
  onSuccess,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [car, setCar] = useState<any>(null);

  // Estados locais dos filtros
  const [isReleased, setIsReleased] = useState(false); // Liberação Total
  const [initiallyReleased, setInitiallyReleased] = useState(false);

  const [releasedEsteiraIds, setReleasedEsteiraIds] = useState<string[]>([]);
  const [initialReleasedEsteiraIds, setInitialReleasedEsteiraIds] = useState<string[]>([]);

  const [plannedEsteiraIds, setPlannedEsteiraIds] = useState<string[]>([]);
  const [note, setNote] = useState('');

  // Confirmação de remoção
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [removalReason, setRemovalReason] = useState('');
  const [removedItemsList, setRemovedItemsList] = useState<string[]>([]);

  // Hook de esteiras do sistema
  const { esteiras, isLoading: esteirasCarregando, isError: esteirasComErro } = useEsteiras(true);

  // Filtrar esteiras ativas que não são de sistema, ordenando por ordem
  const workEsteiras = useMemo(() => {
    return (esteiras || [])
      .filter((e) => !e.sistema && e.ativo)
      .sort((a, b) => a.ordem - b.ordem);
  }, [esteiras]);

  /**
   * 🔴 Sem a lista de esteiras não dá para salvar (regra da at-60: quando a carga falha e a tela
   * permite escrever por cima, avisar não basta — bloquear).
   *
   * O payload é DECLARATIVO: `esteiraIds` é o conjunto completo desejado. Com a lista vazia por
   * erro, o filtro abaixo produziria `[]`, que o backend lê como "remova todas as liberações
   * deste veículo" — apagando em silêncio uma configuração que a tela nem chegou a mostrar.
   */
  const listaDeEsteirasIndisponivel = esteirasComErro || esteirasCarregando;

  // Esteira em que o veículo está agora, quando é esteira de trabalho (as de sistema
  // fluem sem liberação). Usada para avisar que a remoção derruba atividade em andamento.
  const esteiraAtualDeTrabalho = useMemo(
    () => workEsteiras.find((e) => e.id === car?.esteiraId) || null,
    [workEsteiras, car],
  );

  // A esteira atual fica sem liberação depois de salvar? (at-44: nesse caso o backend
  // encerra as sessões de trabalho abertas nela.)
  const esteiraAtualPerdeLiberacao = Boolean(
    esteiraAtualDeTrabalho && !isReleased && !releasedEsteiraIds.includes(esteiraAtualDeTrabalho.id),
  );

  const fetchCarReleaseData = async (id: string) => {
    setLoading(true);
    try {
      const [carData, activitiesData] = await Promise.all([
        getCar(id),
        // Sem sinal, quem libera deixava de ver os serviços em aberto do veículo
        // e liberava às cegas — o erro precisa aparecer.
        getAtividadesCarro({ carId: id, isActive: true }).catch((error) => {
          notify.errorFrom(
            error,
            'Não foi possível carregar os serviços planejados do veículo. Confira antes de liberar; se persistir, acione a TI.',
          );
          return [];
        }),
      ]);

      setCar(carData);

      const totalReleased = Boolean(carData.releasedAt);
      setIsReleased(totalReleased);
      setInitiallyReleased(totalReleased);

      const ids = (carData.esteirasLiberadas || []).map((e: any) => e.esteiraId);
      setReleasedEsteiraIds(ids);
      setInitialReleasedEsteiraIds(ids);

      const plannedIds = (activitiesData || [])
        .map((act: any) => act.esteiraId)
        .filter(Boolean) as string[];
      setPlannedEsteiraIds(plannedIds);

      setNote(carData.releaseNote || '');
    } catch (err) {
      notify.error('Erro ao carregar dados de liberação do veículo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && carId) {
      setConfirmingRemoval(false);
      setRemovalReason('');
      setRemovedItemsList([]);
      void fetchCarReleaseData(carId);
    }
  }, [isOpen, carId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  const persistRelease = async (payload: { released: boolean; esteiraIds?: string[]; note?: string }) => {
    if (!carId) return;
    setSaving(true);
    try {
      const result = await releaseCar(carId, payload);
      const encerradas = Number(result?.sessoesEncerradas ?? 0);
      if (encerradas > 0) {
        notify.success(
          encerradas === 1
            ? 'Liberação atualizada. 1 atividade em andamento foi encerrada e enviada para aprovação do Chefe de Oficina.'
            : `Liberação atualizada. ${encerradas} atividades em andamento foram encerradas e enviadas para aprovação do Chefe de Oficina.`,
        );
      } else {
        notify.success('Liberação atualizada com sucesso!');
      }
      onSuccess();
      onCancel();
    } catch (err: any) {
      notify.error(err.message || 'Erro ao salvar a liberação.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * O modal só declara o que ele CONTROLA: ids que não estão na lista renderizada não podem sair
   * no payload declarativo (BUG-006). Uma liberação para esteira que a tela esconde — de sistema
   * ou desativada — era reenviada a cada save e o backend recusava o próprio dado, travando o
   * veículo sem checkbox nenhum para desfazer.
   *
   * ⚠️ Filtrar aqui, no ENVIO, e nunca ao semear o estado: `useEsteiras` e `fetchCarReleaseData`
   * carregam em paralelo, e na semeadura a lista ainda pode estar vazia.
   */
  const esteiraIdsDeclaraveis = () =>
    releasedEsteiraIds.filter((id) => workEsteiras.some((e) => e.id === id));

  const handleSave = async () => {
    if (listaDeEsteirasIndisponivel) return;

    // Identificar o que está sendo removido para exigir confirmação
    const removedTotal = initiallyReleased && !isReleased;
    const removedEsteiras = initialReleasedEsteiraIds.filter(
      (id) => !releasedEsteiraIds.includes(id)
    );

    if (removedTotal || removedEsteiras.length > 0) {
      const list: string[] = [];
      if (removedTotal) {
        list.push('Liberação total');
      }
      removedEsteiras.forEach((id) => {
        const est = workEsteiras.find((e) => e.id === id);
        if (est) {
          list.push(`Liberação da esteira: ${est.nome}`);
        }
      });
      setRemovedItemsList(list);
      setConfirmingRemoval(true);
      return;
    }

    await persistRelease({
      released: isReleased,
      esteiraIds: esteiraIdsDeclaraveis(),
      note,
    });
  };

  const handleConfirmRemoval = async () => {
    if (listaDeEsteirasIndisponivel) return;
    await persistRelease({
      released: isReleased,
      esteiraIds: esteiraIdsDeclaraveis(),
      note: removalReason.trim() || undefined,
    });
  };

  const handleEsteiraCheckboxChange = (id: string, checked: boolean) => {
    setReleasedEsteiraIds((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      } else {
        return prev.filter((x) => x !== id);
      }
    });
  };

  const handleSelectPlanned = () => {
    setReleasedEsteiraIds((prev) => {
      const union = Array.from(new Set([...prev, ...plannedEsteiraIds]));
      return union;
    });
  };

  const getStatusSummary = () => {
    if (!car) return null;
    const status = car.releaseStatus || (car.releasedAt ? 'TOTAL' : 'PENDENTE');

    if (status === 'TOTAL') {
      return <span className="state-chip total">Total</span>;
    }
    if (status === 'PARCIAL') {
      const count = (car.esteirasLiberadas || []).length;
      return <span className="state-chip parcial">Parcial ({count})</span>;
    }
    return <span className="state-chip pendente">Pendente</span>;
  };

  if (!isOpen) return null;

  return (
    <div className="vehicle-release-modal-overlay">
      <div className="vehicle-release-modal-content" ref={modalRef}>
        <div className="vehicle-release-modal-header">
          <h2>Controle de Liberação</h2>
          <button className="close-button" onClick={onCancel}>
            &times;
          </button>
        </div>

        {loading ? (
          <div className="vehicle-release-modal-loading">Carregando dados...</div>
        ) : confirmingRemoval ? (
          <div className="vehicle-release-modal-body">
            <p style={{ marginBottom: 12 }}>
              Tem certeza que deseja <strong>remover itens da liberação</strong> deste veículo?
              Ele não poderá entrar nas esteiras desmarcadas ou iniciar sessões até ser liberado novamente.
            </p>

            <div className="removed-items-list">
              <span className="input-label" style={{ color: 'var(--tema-texto-vermelho, #991b1b)' }}>Itens sendo removidos:</span>
              <ul>
                {removedItemsList.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            {esteiraAtualPerdeLiberacao && (
              <div className="removal-session-warning">
                <span className="input-label" style={{ color: 'var(--tema-texto-vermelho, #991b1b)' }}>O que acontece com o trabalho em andamento:</span>
                <p>
                  O veículo está na esteira <strong>{esteiraAtualDeTrabalho?.nome}</strong>, que ficará sem
                  liberação. Se houver <strong>atividade em andamento ou pausada</strong> nesta esteira, ela
                  será <strong>encerrada automaticamente</strong>: as horas já trabalhadas são preservadas e
                  seguem para a aprovação do Chefe de Oficina, e o colaborador recebe um aviso. Nenhuma nova
                  atividade poderá ser iniciada até o veículo ser liberado outra vez.
                </p>
              </div>
            )}

            <div className="form-group">
              <label className="input-label">Motivo da remoção (Opcional)</label>
              <input
                type="text"
                placeholder="Ex: Pagamento estornado"
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                maxLength={255}
                className="note-input"
              />
            </div>

            <div className="modal-actions-footer">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setConfirmingRemoval(false)}
                disabled={saving}
              >
                Voltar
              </button>
              <button
                type="button"
                className="save-btn"
                onClick={handleConfirmRemoval}
                disabled={saving || listaDeEsteirasIndisponivel}
              >
                {saving ? 'Removendo...' : 'Confirmar Remoção'}
              </button>
            </div>
          </div>
        ) : (
          <div className="vehicle-release-modal-body">
            {esteirasComErro && (
              <p className="erro-de-tela">
                Não foi possível carregar a lista de esteiras, então as liberações por esteira não
                podem ser exibidas nem alteradas agora. Recarregue a página; se persistir, acione a
                TI.
              </p>
            )}

            <div className="state-summary-row">
              <span className="input-label">Estado Atual:</span>
              {getStatusSummary()}
            </div>

            <div className="form-group" style={{ marginTop: 6 }}>
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={isReleased}
                  onChange={(e) => setIsReleased(e.target.checked)}
                />
                <span className="vehicle-release-checkmark" />
                <span style={{ fontWeight: 600 }}>Liberação total (todas as esteiras de trabalho)</span>
              </label>
            </div>

            <div className="form-group">
              <div className="esteiras-list-header">
                <label className="input-label">Liberações parciais por esteira</label>
                {plannedEsteiraIds.length > 0 && !isReleased && (
                  <button
                    type="button"
                    className="select-planned-btn"
                    onClick={handleSelectPlanned}
                  >
                    Selecionar planejadas
                  </button>
                )}
              </div>

              <div className={`esteiras-container ${isReleased ? 'disabled-list' : ''}`}>
                {workEsteiras.map((est) => {
                  const isChecked = releasedEsteiraIds.includes(est.id);
                  const releasedDetails = (car?.esteirasLiberadas || []).find(
                    (e: any) => e.esteiraId === est.id
                  );
                  const isPlanned = plannedEsteiraIds.includes(est.id);

                  return (
                    <div key={est.id} className="esteira-item-row">
                      <div className="esteira-left">
                        <label className="checkbox-container">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handleEsteiraCheckboxChange(est.id, e.target.checked)}
                          />
                          <span className="vehicle-release-checkmark" />
                        </label>
                        <div className="esteira-color-dot" style={{ backgroundColor: est.cor || 'var(--tema-fundo-3, #ccc)' }} />
                        <div className="esteira-name-info">
                          <span className="esteira-name">{est.nome}</span>
                          {releasedDetails && (
                            <span className="esteira-meta">
                              Por {releasedDetails.releasedBy?.fullName || '-'} em {formatReleaseDate(releasedDetails.releasedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="esteira-right">
                        {isPlanned && <span className="planned-badge">Planejada</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="form-group fade-in">
              <label className="input-label">Observação / Nota de Liberação (Opcional)</label>
              <input
                type="text"
                placeholder="Ex: Documentação conferida e aprovada"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={255}
                className="note-input"
              />
            </div>

            <div className="modal-actions-footer">
              <button
                type="button"
                className="cancel-btn"
                onClick={onCancel}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="save-btn"
                onClick={handleSave}
                disabled={saving || listaDeEsteirasIndisponivel}
                title={
                  esteirasComErro
                    ? 'A lista de esteiras não pôde ser carregada — salvar agora apagaria as liberações por esteira deste veículo.'
                    : undefined
                }
              >
                {saving ? 'Salvando...' : 'Salvar Alteração'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleReleaseModal;
